import React from "react";
import { authorizedFetch } from "./boot.js";
import { Card, Tag } from "./ui.jsx";

async function request(path, input, key) {
  const response = await authorizedFetch(path, input ? {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: key }),
  } : {});
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

function message(error) {
  if (error.message === "delivery_unavailable") return "Invitation sending is not enabled yet. No invitation was issued.";
  if (error.status === 412) return "This invitation changed elsewhere. Refresh before trying again.";
  if (error.status === 400) return "Check the vendor and email address.";
  if (error.status === 404) return "This invitation is not available to this administrator.";
  return "The request could not be confirmed. Retry with the same details to avoid creating a duplicate.";
}

export function OwnerInvitations({ vendor, vendorName }) {
  const [email, setEmail] = React.useState("");
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [version, setVersion] = React.useState(0);
  const [link, setLink] = React.useState("");
  const [owners, setOwners] = React.useState(null);
  const [ownerError, setOwnerError] = React.useState("");
  const attempt = React.useRef(null);
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  React.useEffect(() => { attempt.current = null; setEmail(""); setLink(""); setNotice(""); }, [vendor]);
  React.useEffect(() => {
    let active = true;
    setData(null); setError("");
    request(`/collections/admin.owner_invitations.list@1/?vendor=${encodeURIComponent(vendor)}&limit=50&offset=0`)
      .then(value => { if (active) setData(value); })
      .catch(value => { if (active) setError(message(value)); });
    return () => { active = false; };
  }, [vendor, version]);
  React.useEffect(() => {
    let active = true;
    setOwners(null); setOwnerError("");
    request(`/collections/admin.owner_memberships.list@1/?vendor=${encodeURIComponent(vendor)}`)
      .then(value => { if (active) setOwners(value); })
      .catch(() => { if (active) setOwnerError("Owner access could not be loaded. Refresh to try again."); });
    return () => { active = false; };
  }, [vendor, version]);
  const issue = async event => {
    event.preventDefault();
    if (busy || !email.trim() || data?.deliveryReady !== true) return;
    const normalized = email.trim();
    if (!attempt.current || attempt.current.email !== normalized) {
      attempt.current = { email: normalized, invitation_id: window.crypto.randomUUID(), key: window.crypto.randomUUID() };
    }
    const current = attempt.current;
    setBusy(true); setError(""); setNotice(""); setLink("");
    try {
      const result = await request("/actions/admin.owner_invitations.issue@1/", {
        invitation_id: current.invitation_id, vendor, email: current.email,
      }, current.key);
      if (!mounted.current) return;
      setLink(result.shareUrl || ""); setNotice("Invitation created and queued for email delivery.");
      setEmail(""); attempt.current = null; setVersion(value => value + 1);
    } catch (value) { if (mounted.current) setError(message(value)); }
    finally { if (mounted.current) setBusy(false); }
  };
  const revoke = async row => {
    if (!window.confirm(`Revoke the pending Owner invitation for ${row.email}?`)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await request("/actions/admin.owner_invitations.revoke@1/", {
        invitation_id: row.id, expected_revision: row.revision,
      }, `revoke-${row.id}-${row.revision}`);
      if (!mounted.current) return;
      setLink(""); setNotice("Invitation revoked. Its link can no longer grant access.");
      setVersion(value => value + 1);
    } catch (value) { if (mounted.current) setError(message(value)); }
    finally { if (mounted.current) setBusy(false); }
  };
  const suspend = async row => {
    if (busy || !row.canSuspend || !window.confirm(`Remove ${row.displayName || "this Owner"}'s access to ${vendorName || vendor}? Access to other vendors will stay unchanged.`)) return;
    setBusy(true); setOwnerError(""); setNotice("");
    try {
      await request("/actions/admin.owner_memberships.suspend@1/", {
        vendor, user_id: row.userId, expected_revision: row.revision,
      }, `suspend-${vendor}-${row.userId}-${row.revision}`);
      if (!mounted.current) return;
      setNotice("Owner access removed for this vendor."); setVersion(value => value + 1);
    } catch (value) {
      if (mounted.current) setOwnerError(value.status === 412 ? "Access changed elsewhere. Refresh before trying again." : "Access could not be removed. The last Owner and Inglis administrators are protected; refresh to check current access.");
    } finally { if (mounted.current) setBusy(false); }
  };
  return <Card title="Vendor Owners" subtitle={`Invite an Owner for ${vendorName || vendor}`}>
    <h4>Current access</h4>
    {ownerError && <p role="alert">{ownerError}</p>}
    {!owners && !ownerError && <p role="status">Loading Owner access…</p>}
    {owners?.items?.length === 0 && <p className="muted">No Owners assigned to this vendor.</p>}
    {owners?.items?.map(row => <div className="row" key={row.userId} style={{justifyContent:"space-between",gap:12,flexWrap:"wrap",padding:"12px 0"}}><span>{row.displayName || "Owner"} <Tag>{row.membershipStatus}</Tag>{row.isInglisAdmin && <small className="muted" style={{display:"block"}}>Inglis administrator · managed centrally</small>}</span>{row.canSuspend && <button type="button" className="btn sm" disabled={busy} onClick={() => suspend(row)}>Remove access</button>}</div>)}
    <h4>Invite an Owner</h4>
    <p className="caption">Dev email safeguard: every invitation email goes to deane@inglis.com.au. The invited account must still match the Owner email entered below.</p>
    <p className="caption">The recipient must sign in with this verified email address. Owner access begins only after the one-time link is redeemed.</p>
    <form onSubmit={issue}>
      <label className="field"><span>Owner email address</span><input aria-label="Owner email address" type="email" required maxLength={254} value={email} disabled={busy} onChange={event => { setEmail(event.target.value); attempt.current = null; }}/></label>
      <button className="btn primary" type="submit" disabled={busy || data?.deliveryReady !== true || !email.trim()}>Invite Owner</button>
    </form>
    {data?.deliveryReady === false && <p role="status">Invitation sending is not enabled yet.</p>}
    {error && <p role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {link && <div className="field"><label htmlFor="owner-invitation-link">Invitation link · expires in seven days</label><input id="owner-invitation-link" value={link} readOnly autoComplete="off"/><button type="button" className="btn" onClick={async () => { try { await navigator.clipboard.writeText(link); setNotice("Invitation link copied."); } catch { setNotice("Select the invitation link and copy it manually."); } }}>Copy link</button></div>}
    <h4>Invitations</h4>
    {!data && !error && <p role="status">Loading invitations…</p>}
    {data?.items?.length === 0 && <p className="muted">No Owner invitations for this vendor.</p>}
    {data?.items?.map(row => <div className="row" key={row.id} style={{justifyContent:"space-between",gap:12,flexWrap:"wrap",padding:"12px 0"}}><span>{row.email} <Tag>{row.status}</Tag><small className="muted" style={{display:"block"}}>Expires {new Date(row.expiresAt).toLocaleString()}</small></span>{row.status === "pending" && <button type="button" className="btn sm" disabled={busy} onClick={() => revoke(row)}>Revoke invitation</button>}</div>)}
    <button type="button" className="btn sm" disabled={busy} onClick={() => setVersion(value => value + 1)}>Refresh access and invitations</button>
  </Card>;
}
